import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Search,
  Building,
  Award,
  RefreshCw,
  Calendar,
  Clock,
  Users,
  Target,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const CertificationProcessPage = () => {
  const [activePhase, setActivePhase] = useState('application');

  const processSteps = [
    {
      id: 'inquiry',
      number: '01',
      title: 'Inquiry & Proposal',
      icon: FileText,
      color: '#003f6d',
      duration: '1-2 weeks',
      description: 'You share basic organization details, and we prepare a proposal with estimated audit duration and fees.',
      details: [
        'Provide company name, address, and contact details',
        'Confirm requested standard(s) and certification scope',
        'Share employee count and site locations',
        'Receive proposal with estimated timeline and costs',
        'Review next-step requirements before application'
      ],
      requirement: 'Accurate organization and scope information is required so QSC America can define the audit approach and estimated effort.'
    },
    {
      id: 'application',
      number: '02',
      title: 'Certification Application',
      icon: Search,
      color: '#c1272d',
      duration: 'About 1 week',
      description: 'Submit the application package so we can confirm scope, competence needs, and audit duration.',
      details: [
        'Submit completed application form',
        'Provide company overview and process information',
        'Confirm proposed scope and sites',
        'Provide management system background information',
        'QSC America reviews service feasibility and team needs'
      ],
      requirement: 'For ISO 9001:2015, exclusions are only accepted within the limits of Clause 7 and must be justified.'
    },
    {
      id: 'agreement',
      number: '03',
      title: 'Certification Agreement',
      icon: Building,
      color: '#d4af37',
      duration: 'About 1 week',
      description: 'After proposal acceptance, both parties sign the certification agreement and formalize obligations.',
      details: [
        'Finalize rights and obligations of both parties',
        'Confirm confidentiality commitments',
        'Confirm certification scope and sites',
        'Lock in audit planning assumptions',
        'Schedule Stage 1 and Stage 2 activities'
      ],
      requirement: 'The agreement becomes effective after signatures from both parties.'
    },
    {
      id: 'initial',
      number: '04',
      title: 'Initial Certification Audit (Stage 1 + Stage 2)',
      icon: Award,
      color: '#003f6d',
      duration: 'Stage 1: 1-3 days | Stage 2: 2-5 days',
      description: 'Stage 1 confirms readiness; Stage 2 verifies implementation and effectiveness of the management system.',
      details: [
        'Stage 1: document review, readiness check, and Stage 2 planning',
        'Stage 2: interviews, process observation, and evidence review',
        'Evaluate legal and regulatory compliance',
        'Review internal audits and management reviews',
        'Issue findings (major, minor, or observations) at closing meeting'
      ],
      requirement: 'Stage 2 is normally onsite. Stage 1 may be remote or onsite depending on context and risk.'
    },
    {
      id: 'decision',
      number: '05',
      title: 'Certification Review & Decision',
      icon: RefreshCw,
      color: '#c1272d',
      duration: '1-2 weeks',
      description: 'An independent reviewer evaluates audit evidence and corrective actions before any certification approval.',
      details: [
        'Client submits corrective actions for nonconformities',
        'Independent review is separate from the audit team',
        'Evidence package is evaluated against requirements',
        'Certification is granted only when all requirements are met',
        'Official certificate is issued after approval'
      ],
      requirement: 'Certification cannot be granted until major nonconformities are effectively addressed and verified.'
    },
    {
      id: 'surveillance',
      number: '06',
      title: 'Surveillance Audits',
      icon: Calendar,
      color: '#d4af37',
      duration: 'Typically annual',
      description: 'Surveillance audits are conducted during the 3-year cycle to verify ongoing system effectiveness.',
      details: [
        'Audits occur at regular intervals, usually once per year',
        'Focus on continued effectiveness of key processes',
        'Review changes that affect certified scope',
        'Follow up on prior findings and corrective actions',
        'Maintain confidence in certification status'
      ],
      requirement: 'Surveillance audit duration is typically about one-third of the initial certification audit time.'
    },
    {
      id: 'recertification',
      number: '07',
      title: 'Recertification',
      icon: RefreshCw,
      color: '#003f6d',
      duration: 'Before 3-year expiry',
      description: 'A recertification audit is completed before certificate expiry to determine renewal for another cycle.',
      details: [
        'Review performance across the full certification cycle',
        'Evaluate continued conformity and effectiveness',
        'Assess impact of organizational and scope changes',
        'Issue renewal decision for the next 3-year cycle',
        'Confirm ongoing certification conditions'
      ],
      requirement: 'Recertification audit duration is typically about two-thirds of the initial audit, adjusted for risk and performance.'
    }
  ];

  const timeline = [
    { phase: 'Inquiry & Proposal', duration: '1-2 weeks', description: 'Share organization details and receive proposal' },
    { phase: 'Application', duration: 'About 1 week', description: 'Submit application and scope information' },
    { phase: 'Agreement', duration: 'About 1 week', description: 'Sign certification agreement and finalize plan' },
    { phase: 'Stage 1 Audit', duration: '1-3 days', description: 'Documentation review' },
    { phase: 'Stage 2 Audit', duration: '2-5 days', description: 'Full on-site assessment' },
    { phase: 'Decision', duration: '1-2 weeks', description: 'Independent review and certification decision' },
    { phase: 'Surveillance', duration: 'Annual', description: 'Ongoing audits during 3-year cycle' },
    { phase: 'Recertification', duration: 'Before year 3 expiry', description: 'Renewal audit for next cycle' }
  ];

  const faqs = [
    {
      question: 'What information is needed for the proposal?',
      answer: 'Please provide company details, requested standard(s), certification scope, employee count, and site locations so QSC America can estimate audit duration and fees.'
    },
    {
      question: 'Can Stage 1 be conducted remotely?',
      answer: 'Yes. Stage 1 may be remote or onsite depending on your situation. Stage 2 is normally conducted onsite.'
    },
    {
      question: 'What happens when nonconformities are found?',
      answer: 'You can submit corrective actions. Certification review and decision proceed only after required nonconformities are effectively addressed.'
    },
    {
      question: 'How often are surveillance audits performed?',
      answer: 'Surveillance audits are typically annual during the 3-year certification cycle and focus on ongoing effectiveness and changes.'
    },
    {
      question: 'Can we change scope, add sites, or add standards later?',
      answer: 'Yes. Notify QSC America of requested certification changes. We will review the request and determine any additional audit requirements.'
    },
    {
      question: 'Can we transfer certification from another certification body?',
      answer: 'Yes. If your current certification is with another accredited certification body, QSC America can support the transfer process after review.'
    },
    {
      question: 'How are complaints and appeals handled?',
      answer: 'You may file complaints or appeals at any time. QSC America handles them promptly and impartially in line with documented procedures.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#003f6d] via-[#004a85] to-[#003f6d] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">Certification Process</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-xl text-gray-200 max-w-3xl">
            This page explains each certification step in plain language, from inquiry and proposal through recertification.
          </p>
        </div>
      </section>

      {/* Process Overview Timeline */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Your Certification Journey</h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A step-by-step view of the full certification lifecycle.
            </p>
          </div>

          {/* Timeline Visualization */}
          <div className="relative mb-20">
            <div className="hidden md:block absolute left-0 right-0 top-1/2 h-2 bg-gradient-to-r from-[#003f6d]/20 via-[#d4af37]/30 to-[#003f6d]/20 transform -translate-y-1/2 rounded-full"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-6 md:gap-8 relative">
              {timeline.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="relative z-10 mb-5 flex justify-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#003f6d] to-[#004a85] text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 cursor-pointer">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 text-sm md:text-base leading-tight">{item.phase}</h3>
                  <p className="text-xs md:text-sm text-[#d4af37] font-semibold mb-2 tracking-wide">{item.duration}</p>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Process Steps */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-5xl font-bold text-gray-900 mb-5">Seven-Step Process</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#003f6d] via-[#d4af37] to-[#003f6d] mx-auto mb-7 rounded-full"></div>
            <p className="text-xl text-gray-700 font-medium">
              Select each phase to view key activities, timelines, and requirements.
            </p>
          </div>

          <Tabs value={activePhase} onValueChange={setActivePhase} className="w-full">
            <TabsList className="flex flex-wrap justify-center w-full mb-10 h-auto gap-2 p-2 bg-gray-100 rounded-xl">
              {processSteps.map((step) => {
                const Icon = step.icon;
                // Short labels that fit comfortably inside each pill
                const shortLabels = {
                  inquiry:        'Inquiry & Proposal',
                  application:    'Application',
                  agreement:      'Agreement',
                  initial:        'Initial Audit',
                  decision:       'Review & Decision',
                  surveillance:   'Surveillance',
                  recertification:'Recertification',
                };
                return (
                  <TabsTrigger
                    key={step.id}
                    value={step.id}
                    className="flex flex-col items-center gap-1.5 py-3 px-4 rounded-lg transition-all duration-300 data-[state=active]:bg-[#003f6d] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-gray-200 data-[state=active]:hover:bg-[#002a4d] min-w-[100px] max-w-[140px] text-center"
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span className="text-[11px] font-semibold leading-tight">{shortLabels[step.id]}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {processSteps.map((step) => {
              const Icon = step.icon;
              return (
                <TabsContent key={step.id} value={step.id} className="mt-6">
                  <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow duration-300 border-l-4 overflow-hidden" style={{ borderLeftColor: step.color }}>
                    <CardContent className="p-8 md:p-10">
                      <div className="flex items-start gap-6 mb-8">
                        <div
                          className="p-5 rounded-xl flex-shrink-0"
                          style={{ backgroundColor: `${step.color}15` }}
                        >
                          <Icon className="w-8 h-8" style={{ color: step.color }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-3">
                            <span
                              className="text-5xl font-bold opacity-80"
                              style={{ color: step.color }}
                            >
                              {step.number}
                            </span>
                            <div>
                              <h3 className="text-3xl font-bold text-gray-900 mb-1">{step.title}</h3>
                              <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                                <Clock className="w-4 h-4" style={{ color: step.color }} />
                                <span>{step.duration}</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-lg text-gray-700 leading-relaxed mb-2">{step.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <h4 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                            <CheckCircle className="w-6 h-6" style={{ color: step.color }} />
                            What Happens
                          </h4>
                          <ul className="space-y-3 ml-8">
                            {step.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-gray-700">
                                <ArrowRight className="w-4 h-4 flex-shrink-0 mt-1 opacity-60" style={{ color: step.color }} />
                                <span className="leading-relaxed text-sm md:text-base">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-bold text-lg text-gray-900 flex items-center gap-2 mb-3">
                            <AlertCircle className="w-6 h-6" style={{ color: step.color }} />
                            Important Requirements
                          </h4>
                          <div
                            className="p-6 rounded-lg border-l-4 space-y-3"
                            style={{
                              backgroundColor: `${step.color}08`,
                              borderLeftColor: step.color
                            }}
                          >
                            <p className="text-gray-800 leading-relaxed text-sm md:text-base font-medium">{step.requirement}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Benefits of Our Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-5xl font-bold text-gray-900 mb-5">
              Why Choose QSC America?
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#003f6d] via-[#d4af37] to-[#003f6d] mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: 'Expert Auditors',
                description: 'Experienced professionals with deep industry knowledge and practical insights'
              },
              {
                icon: Target,
                title: 'Transparent Process',
                description: 'Clear communication at every step with no surprises or hidden requirements'
              },
              {
                icon: TrendingUp,
                title: 'Value-Added Approach',
                description: 'We identify improvement opportunities beyond just checking compliance boxes'
              },
              {
                icon: Calendar,
                title: 'Flexible Scheduling',
                description: 'We work around your business operations with remote and on-site options'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border-0 shadow-md">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-[#d4af37]/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#d4af37]/30 transition-colors">
                      <Icon className="w-8 h-8 text-[#003f6d]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-5xl font-bold text-gray-900 mb-5">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#003f6d] via-[#d4af37] to-[#003f6d] mx-auto rounded-full"></div>
          </div>

          <div className="space-y-5">
            {faqs.map((faq, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 border-0 shadow-md hover:border-l-4 hover:border-l-[#d4af37]">
                <CardContent className="p-7">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#d4af37] flex-shrink-0 mt-0.5" />
                    <span className="leading-tight">{faq.question}</span>
                  </h3>
                  <p className="text-gray-700 pl-9 leading-relaxed text-base">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#003f6d] via-[#004a85] to-[#003f6d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-7 leading-tight">
            Ready to Start the Certification Process?
          </h2>
          <p className="text-xl text-gray-200 mb-10 leading-relaxed">
            Contact QSC America to begin with inquiry, proposal, and application planning.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Link to="/contact">
              <Button className="bg-[#d4af37] hover:bg-[#c49b2a] text-[#003f6d] font-bold px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                Start Your Application
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/certification/public-information">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003f6d] px-10 py-6 text-lg font-bold transition-all duration-300 transform hover:scale-105">
                Public Certification Information
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CertificationProcessPage;